"use client"

import React, { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "next/navigation"
import { Campaign, SearchParams } from "@ub/fact-finder-client"
import { useTranslations } from "next-intl"

import { CategorySearchResult } from "@/lib/factfinder/factfinder-interfaces"
import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import FallbackCard from "@/components/common/FallbackCard"
import { GuidedSellingContainer } from "@/components/guided-selling/guided-selling-container"
import { FilterButtonClient } from "@/components/search-and-category/FilterButtonClient"
import FacetList from "@/components/search-and-category/FacetList"
import SearchResultContainer from "@/components/search-and-category/SearchResultContainer"
import { ApplicationSettings } from "@/lib/settings/application-settings"
import { UbBrandResultsStoryblok, UbGlobalGuidedSellingStoryblok } from "@/components/storyblok/component-types"
import HtmlTextBox from "./common/HtmlTextBox"
import { resolveListingApiUrl } from "@/components/storyblok/listing-api-client"

interface BrandListingResponse {
  searchresult?: CategorySearchResult
  ffSearchParams?: SearchParams
  isFiltered: boolean
  brandNotFound?: boolean
  error?: string
}

interface UBBrandResultsClientProps {
  blok: UbBrandResultsStoryblok
  brandSlug: string
  brandName?: string
  brandHeaderUrl?: string
  brandSeoText?: string
  market: string
  channel?: MarketChannel
  sbLocale: string
  isoLocale: string
  defaultFacetsToHide: string[]
  applicationSettings: ApplicationSettings
  guidedSellingSettings?: UbGlobalGuidedSellingStoryblok
  guidedSellingSummaryTop?: React.ReactNode
  guidedSellingSummaryBottom?: React.ReactNode
}

export default function UBBrandResultsClient({
  blok,
  brandSlug,
  brandName,
  brandHeaderUrl,
  brandSeoText,
  market,
  channel = DEFAULT_MARKET_CHANNEL,
  sbLocale,
  isoLocale,
  defaultFacetsToHide,
  applicationSettings,
  guidedSellingSettings,
  guidedSellingSummaryTop,
  guidedSellingSummaryBottom,
}: UBBrandResultsClientProps) {
  const t = useTranslations("storefront")
  const searchParams = useSearchParams()
  const { setIsLoading } = useUbStorefrontState()
  const [data, setData] = useState<BrandListingResponse | undefined>()
  const [error, setError] = useState<string | undefined>()

  const queryString = searchParams.toString()
  const templateBlok = blok.resultDisplayType[0]
  const allowDebugMode = applicationSettings.allowDebugMode ?? false
  const hideAddToBasket = applicationSettings.hideAddToBasketButton ?? false

  useEffect(() => {
    const controller = new AbortController()
    const apiSearchParams = new URLSearchParams(queryString)
    apiSearchParams.set("market", market)
    apiSearchParams.set("channel", channel)
    apiSearchParams.set("brandSlug", brandSlug)

    setIsLoading(true)
    setError(undefined)

    resolveListingApiUrl("brand", apiSearchParams, { sbLocale, channel, signal: controller.signal })
      .then((url) =>
        fetch(url, {
          signal: controller.signal,
        })
      )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text())
        }
        return response.json() as Promise<BrandListingResponse>
      })
      .then(setData)
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : "Unable to load brand results")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [brandSlug, channel, market, queryString, sbLocale, setIsLoading])

  const categorySearchResult = data?.searchresult
  const hitcount = categorySearchResult?.hitcount ?? 0
  const guidedSellingCampaign: Campaign | undefined = useMemo(
    () =>
      blok.showGuidedSellingBlock
        ? (categorySearchResult?.campaigns?.find((campaign) => campaign.flavour === "ADVISOR") ?? undefined)
        : undefined,
    [blok.showGuidedSellingBlock, categorySearchResult?.campaigns]
  )

  if (error) {
    return <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm">{t("category.products.empty")}</div>
  }

  if (!data) {
    return <FallbackCard className="min-h-480 bg-gray-100" />
  }

  if (data.brandNotFound || !hitcount) {
    return <div>{t("category.products.empty")}</div>
  }

  return (
    <>
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-bold">{brandName}</h1>

      <FacetList
        facetManager={undefined}
        isoLocale={isoLocale}
        sbLocale={sbLocale}
        defaultFacetsToHide={defaultFacetsToHide}
        facets={categorySearchResult?.facets ?? []}
        hitcount={hitcount}
        isFiltered={data.isFiltered}
        brandSlug={brandSlug}
        ffSearchParams={data.ffSearchParams}
      />
    </div>

    <div className="@container flex w-full flex-col gap-y-4">
      <img className={"object-contain"} alt={brandName} src={brandHeaderUrl} />

      {guidedSellingCampaign &&
        guidedSellingSettings?.show_guided_selling_block &&
        guidedSellingSettings.label_breadcrumb_start &&
        guidedSellingSettings.label_breadcrumb_end &&
        guidedSellingSettings.label_from_to &&
        guidedSellingSettings.label_or &&
        guidedSellingSettings.label_btn_confirm &&
        guidedSellingSettings.label_btn_reset &&
        guidedSellingSettings.label_btn_open && (
          <GuidedSellingContainer
            campaign={guidedSellingCampaign}
            label_breadcrumb_start={guidedSellingSettings.label_breadcrumb_start}
            label_breadcrumb_end={guidedSellingSettings.label_breadcrumb_end}
            label_from_to={guidedSellingSettings.label_from_to}
            label_or={guidedSellingSettings.label_or}
            label_btn_confirm={guidedSellingSettings.label_btn_confirm}
            label_btn_reset={guidedSellingSettings.label_btn_reset}
            label_btn_open={guidedSellingSettings.label_btn_open}
            blok_summary_top={guidedSellingSummaryTop}
            blok_summary_bottom={guidedSellingSummaryBottom}
            trackingPageType="brand"
            totalHits={hitcount}
            ffSearchParams={categorySearchResult?.effectiveFFParams}
            showCloseButton={true}
            defaultClosed={true}
          />
        )}

      <div className="flex flex-row items-center justify-center bg-transparent lg:hidden">
        <FilterButtonClient />
      </div>

      <SearchResultContainer
        isB2B={true}
        allowDebugMode={allowDebugMode}
        hideAddToBasket={hideAddToBasket}
        inStoryblock={false}
        tracking="brand"
        sbLocale={sbLocale}
        channel={channel}
        isoLocale={isoLocale}
        products={categorySearchResult?.hits ?? []}
        templateBlok={templateBlok}
        productsTotal={hitcount}
        viewItemListPageType="brand"
        searchTerm={`brand_${brandName ?? brandSlug}`}
        campaignId={guidedSellingCampaign?.id}
      />

      {brandSeoText && <HtmlTextBox text={brandSeoText} />}
    </div>
    </>
  )
}
